import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { staffToken } from "@/api/client";
import { LoginPage } from "@/routes/login";
import { MetricsPage } from "@/routes/metrics";
import { OrganizationDetailPage } from "@/routes/organization-detail";
import { OrganizationsPage } from "@/routes/organizations";

const rootRoute = createRootRoute({ component: Outlet });

/**
 * requireStaffToken is a CONVENIENCE, not a security control.
 *
 * It stops a signed-out engineer from watching the console flash a table of nothing before
 * the 401 lands. It decides NOTHING about authorization: the real gate is on the server, it
 * checks `users.is_platform_staff`, and no amount of editing localStorage in a browser gets
 * past it. If this file said `if (user.isStaff)`, that would be an authorization check in a
 * place the user can edit — and it does not.
 */
function requireStaffToken() {
  if (!staffToken.get()) throw redirect({ to: "/login" });
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: staffToken.get() ? "/organizations" : "/login" });
  },
});

const organizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations",
  beforeLoad: requireStaffToken,
  component: OrganizationsPage,
});

const organizationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/organizations/$orgId",
  beforeLoad: requireStaffToken,
  component: OrganizationDetailPage,
});

const metricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/metrics",
  beforeLoad: requireStaffToken,
  component: MetricsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  organizationsRoute,
  organizationDetailRoute,
  metricsRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
